import { Frame, iPointTranslator, LaneLine, RecordType, Vector2, Vector3 } from "@app/shared";
import React, { useMemo } from "react";
import { ChartBounds, DataChart, DatasetConfig, TargetDatasetConfig } from "../DataChart";

export type SelectedTarget = {
    type: "radar" | "video";
    position: Vector3;
    datasetIndex: number;
    pointIndex: number;
};

export enum ViewAxisOptions {
    X,
    Y,
    Z,
}

export type RadarFrameWithOpacity = {
    opacity?: number;
    number?: number;
    frame: Frame;
};
export type VideoFrameWithOpacity = {
    opacity?: number;
    number?: number;
    uvPoints: Vector2[];
};

export type FusionChartProps = {
    videoFrames: VideoFrameWithOpacity[];
    radarFrames: RadarFrameWithOpacity[];
    bounds?: FusionChartBounds;
    staticPointColor?: string;
    targetColors?: string[];
    chartOptions: FusionChartOptions;
    translator: iPointTranslator;
    selectedTargets?: SelectedTarget[];
    onTargetSelect?: (target: SelectedTarget) => void;
    laneLines?: LaneLine[];
};

export type FusionChartOptions = {
    renderRadarPoints: boolean;
    renderRadarTargets: boolean;
    renderBoundingBoxCenters: boolean;
    renderBoundingBoxBottomQuarters: boolean;
    renderLaneLines: boolean;

    pitch: number;
    roll: number;
    fusionOffsetPitchDegrees: number; // Fusion calibration offset separate from accelerometer
    fusionOffsetRollDegrees: number; // Fusion calibration offset separate from accelerometer
    height: number;
    estimatedReturn: number;

    viewAxis: ViewAxisOptions;
};

export type FusionChartBounds = {
    minX?: number;
    maxX?: number;
    minY?: number;
    maxY?: number;
};

function getXAxis(axis: ViewAxisOptions): string {
    switch (axis) {
        case ViewAxisOptions.Z:
            return "X-Axis";
        case ViewAxisOptions.X:
            return "Y-Axis";
        case ViewAxisOptions.Y:
            return "X-Axis";
    }
}
function getYAxis(axis: ViewAxisOptions): string {
    switch (axis) {
        case ViewAxisOptions.Z:
            return "Y-Axis";
        case ViewAxisOptions.X:
            return "Z-Axis";
        case ViewAxisOptions.Y:
            return "Z-Axis";
    }
}
function toSelectedView(vector: Vector3, viewAxis: ViewAxisOptions): Vector3 {
    if (viewAxis === ViewAxisOptions.Y) return new Vector3({ x: -vector.x, y: vector.z, z: vector.y });
    else if (viewAxis === ViewAxisOptions.X) return new Vector3({ x: vector.y, y: vector.z, z: vector.x });
    else return vector;
}

export const FusionChart: React.FC<FusionChartProps> = (props) => {
    // Create video target datasets
    const videoTargetDatasets = useMemo((): TargetDatasetConfig[] => {
        if (!props.videoFrames || !props.translator || !props.chartOptions) return [];

        const datasets: TargetDatasetConfig[] = [];

        for (let i = 0; i < props.videoFrames.length; i++) {
            const frameAge = i + 1 - props.videoFrames.length;
            const videoFrame = props.videoFrames[i];
            if (!videoFrame || !videoFrame.uvPoints) continue;

            const videoData = videoFrame.uvPoints.map((ts, index) => {
                const translated = toSelectedView(props.translator.imageToGround(ts), props.chartOptions.viewAxis);
                return {
                    x: translated.x,
                    y: translated.y,
                    z: translated.z,
                    original: ts,
                    key: "video",
                    pointIndex: index,
                    datasetIndex: i,
                };
            });

            const selectedIndexes = props.selectedTargets?.filter((t) => t.type === "video" && t.datasetIndex === i).map((t) => t.pointIndex) || [];

            datasets.push({
                label: "Video Targets" + (frameAge ? ` (${frameAge})` : ""),
                data: videoData,
                baseColor: "#0000FF",
                selectedColor: "#00FF00",
                baseRadius: 5,
                selectedRadius: 8,
                selectedIndexes,
                borderColor: "#FFFFFF",
                borderWidth: 0,
                selectedBorderWidth: 3,
            });
        }

        return datasets;
    }, [props.videoFrames, props.translator, props.chartOptions.viewAxis, props.selectedTargets]);

    // Create radar target datasets
    const radarTargetDatasets = useMemo((): TargetDatasetConfig[] => {
        if (!props.radarFrames || !props.translator || !props.chartOptions) return [];

        const datasets: TargetDatasetConfig[] = [];

        for (let i = 0; i < props.radarFrames.length; i++) {
            const frameAge = i + 1 - props.radarFrames.length;
            const radarFrame = props.radarFrames[i];
            if (!radarFrame || !radarFrame.frame || !radarFrame.frame.tlvs) continue;

            const trackStates = radarFrame.frame.tlvs.find((tlv) => tlv.getDataType() === RecordType.RadarTarget)?.radarTargets;
            if (trackStates) {
                const radarData = trackStates.map((ts, index) => {
                    const translated = toSelectedView(props.translator.radarToGround(ts.position()), props.chartOptions.viewAxis);
                    return {
                        x: translated.x,
                        y: translated.y,
                        z: translated.z,
                        key: "radar",
                        original: ts.position(),
                        pointIndex: index,
                        datasetIndex: i,
                    };
                });

                const selectedIndexes = props.selectedTargets?.filter((t) => t.type === "radar" && t.datasetIndex === i).map((t) => t.pointIndex) || [];

                datasets.push({
                    label: "Radar Target " + (frameAge ? ` (${frameAge})` : ""),
                    data: radarData,
                    baseColor: "#FF0000",
                    selectedColor: "#FFFF00",
                    baseRadius: 5,
                    selectedRadius: 8,
                    selectedIndexes,
                    borderColor: "#FFFFFF",
                    borderWidth: 0,
                    selectedBorderWidth: 3,
                });
            }
        }

        return datasets;
    }, [props.radarFrames, props.translator, props.chartOptions.viewAxis, props.selectedTargets]);

    // Create point cloud datasets
    const pointCloudDatasets = useMemo((): DatasetConfig[] => {
        if (!props.radarFrames || !props.translator || !props.chartOptions) return [];

        const datasets: DatasetConfig[] = [];

        for (let i = 0; i < props.radarFrames.length; i++) {
            const frameAge = i + 1 - props.radarFrames.length;
            const radarFrame = props.radarFrames[i];
            if (!radarFrame || !radarFrame.frame || !radarFrame.frame.tlvs) continue;

            const pointClouds = radarFrame.frame.tlvs.find((tlv) => tlv.getDataType() === RecordType.PointCloud)?.pointClouds;
            if (pointClouds) {
                const pcData = pointClouds.map((ts) => {
                    const translated = toSelectedView(props.translator.radarToGround(ts.cartesianPosition()), props.chartOptions.viewAxis);
                    return {
                        x: translated.x,
                        y: translated.y,
                        z: translated.z,
                        original: ts.cartesianPosition(),
                    };
                });

                datasets.push({
                    label: "Radar PC " + (frameAge ? ` (${frameAge})` : ""),
                    data: pcData,
                    color: "#CCCCCC",
                    pointRadius: 5,
                    opacity: radarFrame.opacity,
                });
            }
        }

        return datasets;
    }, [props.radarFrames, props.translator, props.chartOptions.viewAxis]);

    const laneLinesDataset = useMemo((): DatasetConfig[] => {
        if (!props.chartOptions.renderLaneLines || !props.laneLines) return [];

        const datasets: DatasetConfig[] = [];
        props.laneLines.forEach((laneLine, i) => {
            if (!laneLine.points || laneLine.points.length < 2) return;

            const lineData = laneLine.points.map((point, index) => ({
                x: point.x,
                y: point.y,
                z: 0,
                pointIndex: index,
            }));

            datasets.push({
                label: `Lane Line ${laneLine.name || i + 1}`,
                data: lineData,
                color: "#AA0044",
                borderColor: "#AA0044",
                borderWidth: 2,
                pointRadius: 2,
                showLine: true,
            });
        });

        return datasets;
    }, [props.laneLines, props.translator, props.chartOptions.viewAxis]);

    // Create coordinate transformation function for lane lines
    const coordinateTransform = useMemo(() => {
        if (!props.translator || !props.chartOptions) {
            return (points: { x: number; y: number; z?: number }[]) => points.map((p) => ({ x: p.x, y: p.y, z: p.z ?? 0 }));
        }
        return (points: { x: number; y: number; z?: number }[]) => {
            return points.map((point) => {
                const groundPoint = new Vector3({ x: point.x, y: point.y, z: point.z ?? 0 });
                const translated = toSelectedView(groundPoint, props.chartOptions.viewAxis);
                return { x: translated.x, y: translated.y, z: translated.z };
            });
        };
    }, [props.chartOptions?.viewAxis, props.translator]);

    // Convert FusionChartBounds to ChartBounds
    const chartBounds: ChartBounds = {
        minX: props.bounds?.minX,
        maxX: props.bounds?.maxX,
        minY: props.bounds?.minY,
        maxY: props.bounds?.maxY,
    };

    const handlePointClick = (datasetIndex: number, pointIndex: number, point: any) => {
        if (!props.onTargetSelect || !point || !point.key) return;

        const target: SelectedTarget = {
            type: point.key,
            position: point.original,
            datasetIndex: point.datasetIndex,
            pointIndex: point.pointIndex,
        };
        props.onTargetSelect(target);
    };

    const getTooltipLabel = (point: any, datasetLabel: string) => {
        const isRadar = datasetLabel?.includes("Radar Target");
        const isVideo = datasetLabel?.includes("Video Targets");

        if (isVideo) {
            return `UV: (${point.original.x?.toFixed(1)}, ${point.original.y?.toFixed(1)}) => Ground: (${point.x?.toFixed(3)}, ${point.y?.toFixed(3)}, ${point.z?.toFixed(3)})`;
        } else if (isRadar) {
            return `Radar: (${point.original.x?.toFixed(3)}, ${point.original.y?.toFixed(3)}, ${point.original.z?.toFixed(3)}) => Ground: (${point.x?.toFixed(3)}, ${point.y?.toFixed(3)}, ${point.z?.toFixed(3)})`;
        } else {
            return `(${point.original?.x?.toFixed(3)}, ${point.original?.y?.toFixed(3)}, ${point.original?.z?.toFixed(3)}) => (${point.x?.toFixed(3)}, ${point.y?.toFixed(3)}, ${point.z?.toFixed(3)})`;
        }
    };

    // Return null if required props are missing
    if (!props.translator || !props.chartOptions) {
        return null;
    }

    return <DataChart targetDatasets={[...videoTargetDatasets, ...radarTargetDatasets]} datasets={[...pointCloudDatasets, ...laneLinesDataset]} bounds={chartBounds} xAxisLabel={getXAxis(props.chartOptions?.viewAxis)} yAxisLabel={getYAxis(props.chartOptions?.viewAxis)} onPointClick={handlePointClick} coordinateTransform={coordinateTransform} customTooltipCallback={getTooltipLabel} height="500px" />;
};
